import {objectFromEntries} from '@benchling/js-utils';
import classNames from 'classnames';
import $ from 'jquery';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {useEffect, useRef} from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ClientId} from 'notebook/ELNConstants';
// @ts-expect-error
import {formatDate} from 'notebook/entries/EntryDayOps';
import {UnknownNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import groupKindToIcon from 'notebook/notes/groups/util/groupKindToIcon';
import NoteConstants from 'notebook/notes/NoteConstants';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {
  BaseNotePart,
  Note,
  NoteDay,
  NoteLinkedObject,
  NoteLinkedObjectType,
  NotePart,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {NoteLinkedAssayData} from 'runs/AttachmentConstants';
import CopyLinkToAnchorButton from 'ui-library/components/CopyLinkToAnchorButton';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import useDocumentScrollState from 'ui-library/useDocumentScrollState';
import APIUtil from 'util/APIUtil';

const JUMP_TO_TODAY_CID = 'today';

const HEADER_TO_LEVEL = {
  [NoteConstants.HEADER_1]: 0,
  [NoteConstants.HEADER_2]: 1,
  [NoteConstants.SUBHEADER_1]: 2,
};

const LEAF_LEVEL = 3;

interface TableOfContentsItem {
  level: number;
  icon?: JSX.Element;
  text: string;
  cid: string;
  isEntryDay: boolean;
  parentSection?: NoteDay;
  sectionToggle: (index: number, isOpen: boolean, provenance_uuid: string) => void;
  provenanceUuid?: string;
}

function iconForNLO(item: NoteLinkedObject): BenchlingIconName | null {
  switch (item.type) {
    case 'registration_table':
      return 'register';
    case 'transfer_table':
      return 'transfer';
    case 'data_table':
      return 'table';
    case 'note_linked_assay_data':
      return ModelTypeConstants.ASSAY_RUN;
    case 'structured_table':
      return getStructuredTableAdapterFromTable(item as UnknownStructuredTable).getTableIcon(
        item as UnknownStructuredTable
      );
    case 'note_linked_chart':
      return 'line-chart';
    case 'note_linked_group':
      return groupKindToIcon((item as UnknownNoteLinkedGroup).kind);
    case NoteLinkedObjectType.NOTE_LINKED_PLATE_RECORD:
      return 'tube-arrow-right';
    default:
      return null;
  }
}

function isNLADWithRunSchema(
  nlad: NoteLinkedAssayData
): nlad is NoteLinkedAssayData & {assayRunSchema: AssayRunSchema} {
  return nlad.assayRunSchema != null;
}

// All NLO types with a discoverable "name" will be rendered in the ToC
function getNoteLinkedObjectDisplayDetails(
  notePart: NotePart & {item: NoteLinkedObject}
): {name: string; iconName: BenchlingIconName | null} | null {
  const {item} = notePart;
  let name: string | undefined;

  // Attachments are NLOs without an `item`
  if (!item) {
    return null;
  }
  // eslint-disable-next-line benchlint/no-in-operator
  if ('name' in item && item.name && item.name.length > 0) {
    name = item.name;
  } else if (item.type === 'note_linked_assay_data') {
    const nlad = item as NoteLinkedAssayData;
    if (isNLADWithRunSchema(nlad)) {
      name = nlad.assayRunSchema.name;
    } else {
      name = nlad.assayResultSchemas[0].name;
    }
    // eslint-disable-next-line benchlint/no-in-operator
  } else if ('config' in item && item.config && item.config.name && item.config.name.length > 0) {
    name = item.config.name;
  }
  return name ? {name, iconName: iconForNLO(item)} : null;
}

function notePartsToTOCItems(
  note: Note,
  getTextForDate: (date: string) => string,
  sectionToggle: (index: number, isOpen: boolean, provenance_uuid: string) => void
): Array<TableOfContentsItem> {
  const daysByIndex = objectFromEntries(note.days?.map((day) => [day.index.toString(), day]) ?? []);
  const items: Array<TableOfContentsItem> = [];
  let parentSection;
  for (let i = 0; i < note.noteParts.length; i++) {
    const notePart = note.noteParts[i];

    // If there is a day at this index, insert that first.
    const thisDay = daysByIndex[i];
    if (thisDay) {
      parentSection = thisDay;
      items.push({
        level: 0,
        text: thisDay.title ? thisDay.title : getTextForDate(thisDay.date),
        cid: thisDay.cid,
        isEntryDay: true,
        sectionToggle,
        provenanceUuid: thisDay.provenanceUuid,
      });
    }

    // Then, check to see if this note part should be in the table of contents.
    const item = notePartToTOCItem(notePart, sectionToggle, parentSection);
    if (item) {
      items.push(item);
    }
  }

  // Dedent the levels to remove any indentation gaps.
  // eslint-disable-next-line no-restricted-syntax
  const levels = _(items)
    .chain()
    .map(({level}) => level)
    .uniq()
    .sort()
    .value();
  const levelToNormalized = _.invert(levels);
  for (const item of items) {
    item.level = levelToNormalized[item.level];
  }
  return items;
}

function notePartToTOCItem(
  notePart: BaseNotePart<ClientId>,
  sectionToggle: (index: number, isOpen: boolean, provenance_uuid: string) => void,
  parentSection?: NoteDay
): TableOfContentsItem | null {
  if (notePart.type === NoteConstants.TABLE) {
    return {
      level: LEAF_LEVEL,
      text: notePart.table!.name,
      cid: notePart.cid,
      icon: <BenchlingIcon icon="note-table" />,
      isEntryDay: false,
      parentSection,
      sectionToggle,
    };
  }

  if (notePart.type === NoteConstants.NOTE_LINKED_OBJECT) {
    const nloDetails = getNoteLinkedObjectDisplayDetails(notePart as NotePart & {item: NoteLinkedObject});
    if (!nloDetails) {
      return null;
    }
    return {
      level: LEAF_LEVEL,
      text: nloDetails.name,
      cid: notePart.cid,
      icon: nloDetails.iconName ? <BenchlingIcon icon={nloDetails.iconName} /> : undefined,
      isEntryDay: false,
      parentSection,
      sectionToggle,
    };
  }

  if (notePart.text) {
    if (!notePart.headerType || HEADER_TO_LEVEL[notePart.headerType] == null) {
      return null;
    }
    return {
      level: HEADER_TO_LEVEL[notePart.headerType],
      text: notePart.text,
      cid: notePart.cid,
      isEntryDay: false,
      parentSection,
      sectionToggle,
    };
  }

  return null;
}

/**
 * Return the target value of `container.scrollTop` such that `node` is scrolled into view, or `null` if the
 * node could not be found or is already in view.
 */
function computeScrollTopToView(container: HTMLElement, node: Element | null): number | null {
  if (!node) {
    return null;
  }
  const containerBox = container.getBoundingClientRect();
  const box = node.getBoundingClientRect();
  if (box.top < containerBox.top) {
    return container.scrollTop + (box.top - containerBox.top);
  } else if (box.bottom > containerBox.bottom) {
    return container.scrollTop + (box.bottom - containerBox.bottom);
  } else {
    return null;
  }
}

function useScrollTOC(scrollTargetCid: string | null): React.RefObject<HTMLDivElement> {
  const tocContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollTargetCid || !tocContainerRef.current) {
      return;
    }
    const tocContainer = tocContainerRef.current;
    const scrollTop =
      scrollTargetCid === JUMP_TO_TODAY_CID
        ? 0
        : computeScrollTopToView(
            tocContainer,
            tocContainer.querySelector(`[data-toc-cid="${scrollTargetCid}"]`)
          );
    if (scrollTop == null) {
      return;
    }
    $(tocContainer).stop().animate({scrollTop}, {duration: 150 /* milliseconds */});
  }, [scrollTargetCid]);

  return tocContainerRef;
}

function JumpToTodayButton({
  cidByDates,
  locked,
  fileType,
  jumpToCid,
  getTextForDate,
}: {
  cidByDates: {[date: string]: string};
  locked: boolean;
  fileType: string;
  jumpToCid: (cid: string) => void;
  getTextForDate: (date: string) => string;
}): JSX.Element | null {
  if (
    Object.keys(cidByDates).length === 0 ||
    locked ||
    APIUtil.isEntryOrSubOrWorkflowTemplateFileType(fileType)
  ) {
    return null;
  }
  return (
    <div className="noteEditor-tableOfContents-jump">
      Jump to{' '}
      <a
        href="#"
        data-toc-cid={JUMP_TO_TODAY_CID}
        onClick={(e) => {
          e.preventDefault();
          jumpToToday(cidByDates, jumpToCid, getTextForDate);
        }}
      >
        today
      </a>
      .
    </div>
  );
}

/**
 * Returns the value of the closest value in the cidByDates to the given dateString.  It's assumed that
 * all of the dates are formatted as 'YYYY-MM-DD' date strings.
 *
 * @param cidByDates: an object whose keys are dates formatted as YYYY-MM-DD strings
 * @param dateString: the needle that we are looking to find in the cidByDates object
 */
export function findCidOfClosestDate(
  cidByDates: {[date: string]: string},
  dateString: string
): {cid: string; closestDate: string} {
  const dates = Object.keys(cidByDates);
  const todayIndex = dates.indexOf(dateString);
  const jumpToDayIndex = todayIndex > -1 ? todayIndex : Math.max(0, _.sortedIndex(dates, dateString) - 1);
  return {cid: cidByDates[dates[jumpToDayIndex]], closestDate: dates[jumpToDayIndex]};
}

function jumpToToday(
  cidByDates: {[date: string]: string},
  jumpToCid: (cid: string) => void,
  getTextForDate: (date: string) => string
): void {
  const todayIsodate = formatDate(moment());
  const {cid, closestDate} = findCidOfClosestDate(cidByDates, todayIsodate);

  if (!closestDate) {
    notifications.warn('Entry does not contain any dates');
    return;
  }

  if (todayIsodate !== closestDate) {
    notifications.info(
      <>
        Jumping to nearest day to today:
        <br />
        <strong>{getTextForDate(closestDate)}</strong>
      </>,
      {
        id: 'TableOfContents',
      }
    );
  }
  jumpToCid(cid);
}

interface TableOfContentsProps {
  note: Note;
  getTextForDate: (date: string) => string;
  getElementByScrollId: (cid: string) => HTMLElement | null;
  locked: boolean;
  documentContainer?: HTMLElement | null;
  fileType: string;
  sectionToggle: (index: number, isOpen: boolean, provenance_uuid: string) => void;
}

interface TableOfContentsScrollManagerProps extends Omit<TableOfContentsProps, 'note'> {
  cidByDates: {[date: string]: string};
  items: Array<TableOfContentsItem>;
  cids: Array<string>;
}

export const TableOfContentsScrollManager = React.memo((props: TableOfContentsScrollManagerProps) => {
  const {scrollPosition, jumpToScrollId} = useDocumentScrollState(
    props.cids,
    props.getElementByScrollId,
    props.documentContainer ?? null
  );

  // If we are selecting the first section, ensure that we scroll up to include "Jump to today".
  const tocScrollTargetCid =
    scrollPosition?.scrollId === props.cids[0] ? JUMP_TO_TODAY_CID : scrollPosition?.scrollId;
  const tocContainerRef = useScrollTOC(tocScrollTargetCid ?? null);
  return (
    <div className="noteEditor-tableOfContents-container" ref={tocContainerRef}>
      <div className="noteEditor-tableOfContents">
        <div className="noteEditor-tableOfContents-content">
          {props.items.length === 0 ? (
            <span className="noteEditor-tableOfContents-emptyText">
              Use headers to fill in the table of contents.
            </span>
          ) : null}
          <JumpToTodayButton
            cidByDates={props.cidByDates}
            locked={props.locked}
            fileType={props.fileType}
            jumpToCid={jumpToScrollId}
            getTextForDate={props.getTextForDate}
          />
          {props.items.map((item) => (
            <TOCItem
              {...item}
              key={item.cid}
              isSelected={scrollPosition != null && item.cid === scrollPosition.scrollId}
              jumpToCid={jumpToScrollId}
              sectionToggle={props.sectionToggle}
            />
          ))}
        </div>
      </div>
    </div>
  );
}, isEqual);

export default function TableOfContents(props: TableOfContentsProps): JSX.Element {
  const items = notePartsToTOCItems(props.note, props.getTextForDate, props.sectionToggle);
  const cids = items.map((item) => item.cid);
  const cidByDates =
    props.note.days == null
      ? {}
      : props.note.days.reduce(
          (
            acc: {
              [date: string]: string;
            },
            day
          ) => (!day.date || acc[day.date] ? acc : {...acc, [day.date]: day.cid}),
          {}
        );
  const propsWithoutNote = {...props, note: undefined};
  return (
    <TableOfContentsScrollManager {...propsWithoutNote} cidByDates={cidByDates} items={items} cids={cids} />
  );
}

function TOCItem({
  cid,
  level,
  icon,
  text,
  isEntryDay,
  isSelected,
  jumpToCid,
  parentSection,
  sectionToggle,
  provenanceUuid,
}: TableOfContentsItem & {
  isSelected: boolean;
  jumpToCid: (cid: string, sectionIndex?: number, sectionUuid?: string) => void;
}): JSX.Element {
  return (
    <a
      onClick={() => {
        if (parentSection?.index != null && parentSection?.provenanceUuid) {
          sectionToggle(parentSection?.index, true, parentSection?.provenanceUuid);
        }
        _.defer(() => {
          jumpToCid(cid);
        });
      }}
      data-toc-cid={cid}
      className={classNames(
        `noteEditor-tableOfContents-item noteEditor-tableOfContents-item--level${level}`,
        {
          'noteEditor-tableOfContents-item--persistentCircle': isEntryDay,
          'noteEditor-tableOfContents-item--selected': isSelected,
        }
      )}
    >
      {icon ? <span className="noteEditor-tableOfContents-item-icon">{icon}</span> : null}
      <span className="noteEditor-tableOfContents-item-text">
        {text}
        <CopyLinkToAnchorButton anchorValue={provenanceUuid} />
      </span>
    </a>
  );
}

export const _TOCItem = TOCItem; // exported for testing

TableOfContents.propTypes = {
  note: PropTypes.object.isRequired,
  getTextForDate: PropTypes.func.isRequired,
  getElementByScrollId: PropTypes.func.isRequired,
  fileType: PropTypes.string.isRequired,
  documentContainer: PropTypes.object,
};
